import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    EventsInPlayOrUpcoming,
    PromotedEventsWidget,
    NextRacesHomePage,
    EventsStartingSoon,
} from 'src/domains/sportsbook/shared/Components';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { QuickLinkTrayContainer } from 'src/domains/layouts/webview/components/quickLinkTray/QuickLinkTray';
import { CarouselPromo } from 'src/domains/layouts/webview/components/homePageCarouselPromos/carouselPromo/CarouselPromo';
import { useAppStateContext } from 'src/appState/AppState';
import { HeroWidget } from 'src/domains/layouts/webview/components/heroEvents/heroWidget/HeroWidget';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { TopEvents } from 'src/domains/sportsbook/webview/components/containers/topEvents/TopEvents';
import { VirtualGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/virtuals/VirtualGamesHomepageLazyLoading';
import { CasinoGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/casino/CasinoGamesHomepageLazyLoading';
import { HomepageWidgetType } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { XtremePushBanner } from '../../webview/components/xtremePush/xtremePushBanner/XtremePushBanner';

type WidgetEntry = {
    component: React.ReactNode;
    condition?: boolean;
};

export const HomepageContent = observer('HomepageContent', () => {
    const { appSportsBookState } = useAppStateContext();
    const common = useCommon();
    const { promotedEventsState } = AppLayoutsState.get(common);
    const appCasinoState = AppCasinoState.get(common);

    const config = ConfigComponents.get(common).config;
    const { hasGamesOnHomepage, hiddenSportsList, startingSoonEvents, topEvents, xtremePush } = config;
    const isRacingHiddenForOperator =
        hiddenSportsList.includes('horseracing') && hiddenSportsList.includes('greyhoundracing');
    const isFirstHRLoadingCompleted = appSportsBookState.raceSlider.get('homePageWidget').isFirstHRLoadingCompleted;

    const widgetMap = new Map<HomepageWidgetType, WidgetEntry>([
        ['promoCarousel', { component: <CarouselPromo modeType='homepage' /> }],
        ['quickLinkTray', { component: <QuickLinkTrayContainer /> }],
        ['heroWidget', { component: <HeroWidget /> }],
        ['xtremePush', { component: <XtremePushBanner type='sportsbook' />, condition: xtremePush === true }],
        ['racingWidget', { component: <NextRacesHomePage />, condition: isRacingHiddenForOperator === false }],
        ['topEvents', { component: <TopEvents />, condition: topEvents === true }],
        ['inPlay', { component: <EventsInPlayOrUpcoming showPriceLegend={false} /> }],
        [
            'startingSoon',
            {
                component: <EventsStartingSoon showPriceLegend={false} />,
                condition: startingSoonEvents === true,
            },
        ],
        [
            'casinoGames',
            {
                component: <CasinoGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true,
            },
        ],
        [
            'promotedEvents',
            {
                component: <PromotedEventsWidget promotedEventsState={promotedEventsState} />,
                condition: isFirstHRLoadingCompleted === true && topEvents === false,
            },
        ],
        [
            'virtualGames',
            {
                component: <VirtualGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true,
            },
        ],
        ['priceChangeLegend', { component: <PriceChangeLegend isRace={false} /> }],
        ['seoPagesContent', { component: <SeoPagesContent pageSlug='homepage' /> }],
    ]);

    const widgetsOrder = [...new Set(config.homepageWidgetsOrder)];

    return (
        <>
            {widgetsOrder.map((key) => {
                const widget = widgetMap.get(key);

                if (!widget || widget.condition === false) {
                    return null;
                }

                return <React.Fragment key={key}>{widget.component}</React.Fragment>;
            })}
        </>
    );
});
